<template>
  <div class="rmr-page-shell airflow-alerts-page">
    <rmr-page-hero
      title="Alertes"
      title-accent="Airflow"
      subtitle="Configurez les destinataires prévenus lorsqu'un workflow échoue."
      :icon="icons.mdiBellAlertOutline"
    >
      <template #actions>
        <rmr-action-button
          v-if="activeSelection.length"
          label="Configurer les destinataires"
          :icon="icons.mdiPencil"
          variant="translucent"
          @click="openEditor()"
        />
        <rmr-action-button
          v-if="configuredSelection.length"
          label="Désactiver"
          :icon="icons.mdiTrashCan"
          :color="colors.danger"
          @click="disableDialog = true"
        />
      </template>
    </rmr-page-hero>

    <section class="rmr-page-shell__content airflow-alerts-page__content">
      <v-card flat class="rmr-table-shell">
        <div class="rmr-table-shell__header">
          <div class="rmr-table-shell__topline">
            <div class="rmr-table-shell__heading">
              <div class="rmr-table-shell__eyebrow">Système</div>
              <div class="rmr-table-shell__title">Workflows surveillés</div>
            </div>
            <div class="rmr-table-shell__controls">
              <div
                class="rmr-table-shell__filter-item airflow-alerts-page__owner-filter"
              >
                <label class="rmr-table-shell__filter-label"
                  >Propriétaire</label
                >
                <rmr-select
                  v-model="ownerFilter"
                  :items="ownerOptions"
                  label="Tous les propriétaires"
                  clearable
                  no-data-text="Aucun propriétaire disponible"
                />
              </div>
              <div class="rmr-table-shell__filter-item rmr-table-shell__search">
                <label class="rmr-table-shell__filter-label">Recherche</label>
                <rmr-text-field
                  v-model="search"
                  :append-icon="icons.mdiMagnify"
                  label="Rechercher un DAG"
                  clearable
                />
              </div>
            </div>
          </div>
        </div>

        <v-card-text class="rmr-table-shell__body">
          <v-data-table
            v-model="selected"
            class="rmr-data-table airflow-alerts-page__table"
            :headers="headers"
            :items="filteredItems"
            :loading="loading"
            :search="search"
            item-key="dag_id"
            locale="fr-FR"
            show-select
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
          >
            <template #header.data-table-select="{ props, on }">
              <v-simple-checkbox
                v-bind="props"
                :color="colors.primary"
                v-on="on"
              />
            </template>

            <template #item.data-table-select="{ isSelected, select }">
              <v-simple-checkbox
                :value="isSelected"
                :color="colors.primary"
                @input="select"
              />
            </template>

            <template #loading>
              <div class="rmr-data-table__state">
                <v-progress-circular
                  indeterminate
                  color="#6366F1"
                  size="28"
                  class="mb-2"
                />
                <div class="rmr-data-table__state-title">
                  Chargement des alertes Airflow
                </div>
              </div>
            </template>

            <template #no-data>
              <div class="rmr-data-table__state">
                <div class="rmr-data-table__state-title">Aucun DAG trouvé</div>
                <div class="rmr-data-table__state-text">
                  Modifiez votre recherche ou attendez le chargement des
                  workflows.
                </div>
              </div>
            </template>

            <template #item.dag_id="{ item }">
              <div class="airflow-alerts-page__dag-id">{{ item.dag_id }}</div>
              <div v-if="!item.is_active" class="airflow-alerts-page__inactive">
                DAG inactif ou supprimé
              </div>
            </template>

            <template #item.owners="{ item }">
              <span class="airflow-alerts-page__owner">
                {{ (item.owners || []).join(", ") || "—" }}
              </span>
            </template>

            <template #item.schedule="{ item }">
              <span class="airflow-alerts-page__schedule">
                {{ item.schedule || "—" }}
              </span>
            </template>

            <template #item.recipients="{ item }">
              <rmr-keyword-chips
                v-if="item.recipients.length"
                :keywords="item.recipients"
                :max-visible="2"
                variant="default"
              />
              <span v-else class="airflow-alerts-page__muted">—</span>
            </template>

            <template #item.status="{ item }">
              <rmr-status-chip
                v-if="item.configuration_error"
                label="Configuration invalide"
                variant="error"
                size="sm"
              />
              <rmr-status-chip
                v-else-if="item.configured"
                label="Activée"
                variant="success"
                size="sm"
              />
              <rmr-status-chip
                v-else
                label="Inactive"
                variant="neutral"
                size="sm"
              />
            </template>

            <template #item.actions="{ item }">
              <div class="rmr-data-table__actions">
                <rmr-icon-button
                  :icon="icons.mdiPencil"
                  variant="edit"
                  tooltip="Configurer les destinataires"
                  :disabled="!item.is_active"
                  @click="openEditor(item)"
                />
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </section>

    <rmr-modal
      v-model="editorDialog"
      variant="form"
      :icon="icons.mdiBellAlertOutline"
      eyebrow="Alertes Airflow"
      :title="editorTitle"
      :subtitle="editorSubtitle"
      max-width="680"
      @close="closeEditor"
    >
      <template #body>
        <v-card-text class="airflow-alerts-page__modal-body">
          <rmr-notice
            v-if="editingItems.length > 1"
            title="Configuration groupée"
            variant="warning"
          >
            Cette liste remplacera les destinataires actuels des
            {{ editingItems.length }} DAGs sélectionnés.
          </rmr-notice>

          <div class="airflow-alerts-page__field">
            <label class="airflow-alerts-page__field-label"
              >Adresses e-mail</label
            >
            <rmr-combobox
              :value="recipientInput"
              size="md"
              label="Destinataires *"
              multiple
              chips
              small-chips
              deletable-chips
              :items="[]"
              :error-messages="recipientErrors"
              :hide-details="'auto'"
              persistent-hint
              hint="Saisissez une adresse puis appuyez sur Entrée. Les virgules et points-virgules sont aussi acceptés."
              @input="setRecipientInput"
            />
          </div>
        </v-card-text>
      </template>

      <template #footer>
        <v-card-actions class="airflow-alerts-page__modal-actions">
          <v-spacer />
          <rmr-action-button
            label="Annuler"
            variant="ghost"
            :disabled="saving"
            @click="closeEditor"
          />
          <rmr-action-button
            label="Enregistrer"
            :icon="icons.mdiContentSaveOutline"
            :loading="saving"
            :disabled="!canSave"
            @click="saveConfiguration"
          />
        </v-card-actions>
      </template>
    </rmr-modal>

    <rmr-confirm-dialog
      v-model="disableDialog"
      title="Désactiver les alertes ?"
      :message="`Les destinataires seront supprimés pour ${configuredSelection.length} DAG(s).`"
      confirm-label="Désactiver"
      cancel-label="Annuler"
      danger
      @confirm="disableSelected"
    />
  </div>
</template>

<script>
import {
  mdiBellAlertOutline,
  mdiContentSaveOutline,
  mdiMagnify,
  mdiPencil,
  mdiTrashCan,
} from "@mdi/js";
import axios from "@axios";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrKeywordChips from "@/components/ui/feedback/RmrKeywordChips.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrCombobox from "@/components/ui/forms/RmrCombobox.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import { RMR_DANGER, RMR_INDIGO } from "@/styles/tokens";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "AirflowAlerts",
  components: {
    RmrActionButton,
    RmrConfirmDialog,
    RmrIconButton,
    RmrKeywordChips,
    RmrModal,
    RmrNotice,
    RmrPageHero,
    RmrStatusChip,
    RmrCombobox,
    RmrSelect,
    RmrTextField,
  },
  data() {
    return {
      items: [],
      selected: [],
      editingItems: [],
      search: "",
      ownerFilter: null,
      loading: false,
      saving: false,
      editorDialog: false,
      disableDialog: false,
      recipientInput: [],
      colors: {
        danger: RMR_DANGER,
        primary: RMR_INDIGO,
      },
      icons: {
        mdiBellAlertOutline,
        mdiContentSaveOutline,
        mdiMagnify,
        mdiPencil,
        mdiTrashCan,
      },
      headers: [
        { text: "DAG", value: "dag_id", width: "28%" },
        {
          text: "Propriétaire",
          value: "owners",
          sortable: false,
          width: "14%",
        },
        {
          text: "Planification",
          value: "schedule",
          sortable: false,
          width: "18%",
        },
        {
          text: "Destinataires",
          value: "recipients",
          sortable: false,
          width: "25%",
        },
        { text: "Statut", value: "status", sortable: false, width: "110px" },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "end",
          width: "56px",
        },
      ],
    };
  },
  computed: {
    ownerOptions() {
      return [...new Set(this.items.flatMap((item) => item.owners || []))].sort(
        (firstOwner, secondOwner) => firstOwner.localeCompare(secondOwner)
      );
    },
    filteredItems() {
      if (!this.ownerFilter) return this.items;
      return this.items.filter((item) =>
        (item.owners || []).includes(this.ownerFilter)
      );
    },
    parsedRecipients() {
      const seen = new Set();
      return this.recipientInput
        .flatMap((value) => String(value || "").split(/[,;\n]+/))
        .map((value) => value.trim())
        .filter((value) => {
          const key = value.toLowerCase();
          if (!value || seen.has(key)) return false;
          seen.add(key);
          return EMAIL_PATTERN.test(value);
        });
    },
    invalidRecipients() {
      return this.recipientInput
        .flatMap((value) => String(value || "").split(/[,;\n]+/))
        .map((value) => value.trim())
        .filter((value) => value && !EMAIL_PATTERN.test(value));
    },
    recipientErrors() {
      if (!this.invalidRecipients.length) return [];
      return [`Adresse(s) invalide(s) : ${this.invalidRecipients.join(", ")}`];
    },
    canSave() {
      return (
        this.editingItems.length > 0 &&
        this.parsedRecipients.length > 0 &&
        this.invalidRecipients.length === 0 &&
        !this.saving
      );
    },
    configuredSelection() {
      return this.selected.filter((item) => item.configured);
    },
    activeSelection() {
      return this.selected.filter((item) => item.is_active);
    },
    editorTitle() {
      if (this.editingItems.length === 1) return this.editingItems[0].dag_id;
      return `${this.editingItems.length} DAGs sélectionnés`;
    },
    editorSubtitle() {
      return this.editingItems.length === 1
        ? "Choisissez les personnes qui recevront une alerte en cas d'échec final."
        : "La même liste de destinataires sera appliquée à tous les DAGs sélectionnés.";
    },
  },
  mounted() {
    this.loadConfigurations();
  },
  methods: {
    async notify(text, color = "success") {
      await this.$store.dispatch("app/setSnackBar", {
        show: true,
        text,
        color,
        timeout: 5000,
      });
    },
    async loadConfigurations() {
      this.loading = true;
      try {
        const { data } = await axios.get("/airflow/alert-configurations");
        this.items = data.items || [];
      } catch (error) {
        await this.notify(
          "Impossible de charger les alertes Airflow.",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    openEditor(item = null) {
      this.editingItems = item ? [item] : [...this.activeSelection];
      this.recipientInput =
        this.editingItems.length === 1
          ? [...this.editingItems[0].recipients]
          : [];
      this.editorDialog = true;
    },
    setRecipientInput(values) {
      this.recipientInput = (Array.isArray(values) ? values : [values])
        .flatMap((value) => String(value || "").split(/[,;\n]+/))
        .map((value) => value.trim())
        .filter(Boolean);
    },
    closeEditor() {
      this.editorDialog = false;
      this.editingItems = [];
      this.recipientInput = [];
    },
    async saveConfiguration() {
      if (!this.canSave) return;
      this.saving = true;
      let updated = 0;
      try {
        for (const item of this.editingItems) {
          await axios.put(
            `/airflow/alert-configurations/${encodeURIComponent(item.dag_id)}`,
            { recipients: this.parsedRecipients }
          );
          updated += 1;
        }
        await this.notify(`${updated} configuration(s) enregistrée(s).`);
        this.closeEditor();
        this.selected = [];
      } catch (error) {
        await this.notify(
          `${updated} configuration(s) enregistrée(s), puis une erreur est survenue.`,
          "error"
        );
      } finally {
        this.saving = false;
        await this.loadConfigurations();
      }
    },
    async disableSelected() {
      this.saving = true;
      let deleted = 0;
      try {
        for (const item of this.configuredSelection) {
          await axios.delete(
            `/airflow/alert-configurations/${encodeURIComponent(item.dag_id)}`
          );
          deleted += 1;
        }
        await this.notify(`${deleted} alerte(s) désactivée(s).`);
        this.disableDialog = false;
        this.selected = [];
      } catch (error) {
        await this.notify(
          `${deleted} alerte(s) désactivée(s), puis une erreur est survenue.`,
          "error"
        );
      } finally {
        this.saving = false;
        await this.loadConfigurations();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.airflow-alerts-page__content {
  padding-bottom: $rmr-space-xl;
}

.airflow-alerts-page__owner-filter {
  width: 220px;
  flex: 0 0 220px;
}

.airflow-alerts-page__table {
  ::v-deep .v-data-table-header th {
    font-size: $rmr-fs-sm !important;
    padding: 11px 12px !important;
  }

  ::v-deep tbody td {
    color: $rmr-text-medium;
    font-size: $rmr-fs-body;
    line-height: 1.4;
    padding: 11px 12px !important;
  }

  ::v-deep .rmr-keyword-chips__chip {
    font-size: $rmr-fs-sm !important;
  }
}

.airflow-alerts-page__dag-id {
  color: $rmr-indigo-accent;
  font-size: $rmr-fs-body;
  font-weight: 800;
  line-height: 1.35;
  word-break: break-word;
}

.airflow-alerts-page__owner {
  color: $rmr-text-medium;
  font-size: $rmr-fs-base;
  font-weight: 700;
}

.airflow-alerts-page__schedule {
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
}

.airflow-alerts-page__inactive {
  color: $rmr-danger;
  font-size: $rmr-fs-sm;
  font-weight: 600;
  margin-top: 2px;
}

.airflow-alerts-page__muted {
  color: $rmr-text-subtle;
}

.airflow-alerts-page__modal-body {
  padding: 24px !important;
}

.airflow-alerts-page__field {
  margin-top: 22px;
}

.airflow-alerts-page__field-label {
  display: block;
  color: $rmr-text-subtle;
  font-size: $rmr-fs-sm;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.airflow-alerts-page__field-label {
  margin-bottom: 7px;
}

.airflow-alerts-page__modal-actions {
  padding: 14px 24px !important;
}

@media (max-width: 960px) {
  .airflow-alerts-page__owner-filter {
    width: 100%;
    flex-basis: 100%;
  }

  .airflow-alerts-page__modal-body {
    padding: 20px 16px !important;
  }

  .airflow-alerts-page__modal-actions {
    padding: 12px 16px !important;
  }
}
</style>
